import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { Accordion } from "react-bootstrap";
import { scrollToElement } from "utils/customHooks/scrollToElement";

const AccardionContext = createContext();

export const useAccordionWizard = () => useContext(AccardionContext);

const accardionReducer = (state, action) => {
  const accordionKeys = state.accordionKeys;
  const accordionRefs = state.accordionRefs;
  switch (action.type) {
    case "register":
      /**
       * payload[0]: accordion key
       * payload[1]: accordion element (ref), to scroll on it on next (if scrollOnChange enabled)
       */
      if (!accordionKeys.includes(action.payload[0])) {
        return {
          ...state,
          accordionKeys: [...accordionKeys, action.payload[0]],
          accordionRefs: [...accordionRefs, action.payload[1]],
        };
      }
      return state;
    default:
      return { ...state, ...action };
  }
};

export const AccordionProvider = ({
  active,
  children,
  scrollOnChange = false,
  ...props
}) => {
  const [accardionState, dispatch] = useReducer(accardionReducer, {
    active,
    accordionKeys: [],
    accordionRefs: [],
  });

  const toStep = useCallback(
    (active) => {
      dispatch({ active });
    },
    [dispatch, accardionState] // eslint-disable-line
  );

  const close = useCallback(() => {
    toStep(null);
  }, [toStep]); // eslint-disable-line

  const next = useCallback(() => {
    const current = accardionState.accordionKeys.indexOf(accardionState.active);
    if (accardionState.accordionKeys.length > current + 1) {
      const nextStep = accardionState.accordionKeys[current + 1];
      if (nextStep) {
        const nextAccordionElement = accardionState.accordionRefs[current + 1];
        handleScroll(nextAccordionElement);
        toStep(nextStep);
      }
    } else {
      close();
    }
  }, [dispatch, accardionState.accordionKeys, accardionState.active, close]); // eslint-disable-line

  const register = useCallback(
    (accordionKey, ref) => {
      dispatch({ payload: [accordionKey, ref], type: "register" });
    },
    [dispatch] // eslint-disable-line
  );

  const accardionContextValue = useMemo(() => {
    return {
      ...accardionState,
      toStep,
      next,
      register,
      close,
    };
  }, [toStep, next, register, accardionState, close]);

  const handleScroll = (accordionHeaderElement) => {
    if (scrollOnChange && accordionHeaderElement) {
      if (accordionHeaderElement) {
        setTimeout(() => {
          scrollToElement(accordionHeaderElement);
        }, 350);
      }
    }
  };

  return (
    <AccardionContext.Provider value={accardionContextValue}>
      <Accordion
        defaultActiveKey={active}
        activeKey={accardionState.active}
        onSelect={(nextAccordion) => {
          toStep(nextAccordion);
        }}
        {...props}
      >
        {children}
      </Accordion>
    </AccardionContext.Provider>
  );
};
